import { v3, type V3 } from "backoffice-api"
import { isFetchError } from "fetcher"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useCurrentUserContext } from "../AppShell/CurrentUserProvider"
import type { ChatMessage } from "./AIChat"
import { Chat } from "./components/Chat"
import { useAIChatContext } from "./providers/AIChatProvider"

type AiAssistantMessage = V3["getAiAssistantMessages"]["data"][number]
type AiAssistantEvent = V3["getAiAssistantEvents"]["data"][number]
type LoadedConversation = Awaited<ReturnType<typeof loadConversation>>

// Polls every second while an answer is being written, easing off once a turn
// has been silent for a minute. There is deliberately no give-up: a valid turn
// can take up to ~13 minutes server-side, and closing the chat stops polling.
const POLL_MS = 1_000
const SLOW_POLL_MS = 3_000
const EMPTY_POLLS_BEFORE_SLOWING = 60

// The backend's `error.payload.message` is English only, so the client
// localizes by `type` and never renders the message.
const ERROR_KEYS = {
  timed_out: "ai_assistant_chat.ERROR_TIMED_OUT",
  interrupted: "ai_assistant_chat.ERROR_INTERRUPTED",
  unavailable: "ai_assistant_chat.ERROR_UNAVAILABLE",
  too_long: "ai_assistant_chat.ERROR_TOO_LONG",
} as const

// Learner chat on the AI Gateway (ENGMAN-378), through the v3 conversation
// endpoints. A turn is accepted with a 202 and answered in the background; the
// answer is read from the conversation's event log from the cursor handed back.
//
// The session is held in local state rather than in the query cache: it is one
// learner's live transcript, and a background refetch replacing it mid-answer
// would be a bug, not freshness.
export const GatewayChat = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) => {
  const { t } = useTranslation()
  const { chatConfig, allChatConfigs, selectAssistant } = useAIChatContext()
  const { currentUser } = useCurrentUserContext()
  const assistantId = chatConfig?.id ?? null

  // While a turn is in flight, the last message is its answer: the preview
  // grows from `token_batch` and is replaced by `done`.
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [isAnswering, setIsAnswering] = useState(false)
  const [isResumeSettled, setIsResumeSettled] = useState(false)
  const [isReloading, setIsReloading] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const cursorRef = useRef(0)

  const createConversation = v3.createAiAssistantConversation.useMutation()
  const createMessage = v3.createAiAssistantMessage.useMutation()

  const genericError = t("ai_assistant_chat.ERROR_GENERIC")

  // Takes over a conversation as the server has it. Only a running one has a
  // turn behind its resume cursor; on any other the log after the cursor is
  // empty, and polling it would never end.
  const adoptConversation = (loaded: NonNullable<LoadedConversation>) => {
    const isRunning = loaded.conversation.status === "running"

    cursorRef.current = loaded.cursor
    setConversationId(loaded.conversation.id)
    setMessages([
      ...toChatMessages(loaded.history),
      ...(isRunning ? [answerPlaceholder()] : []),
    ])
    setIsAnswering(isRunning)
  }

  // Resume the latest conversation the first time the chat is opened.
  // biome-ignore lint/correctness/useExhaustiveDependencies: adoptConversation only calls state setters and writes a ref
  useEffect(() => {
    if (!isOpen || !assistantId || isResumeSettled) return

    let cancelled = false

    loadConversation(assistantId)
      .then((latest) => {
        if (cancelled) return
        setIsResumeSettled(true)
        if (latest) adoptConversation(latest)
      })
      .catch(() => {
        // No history is not worth blocking the chat for: start fresh. At
        // worst that is a new conversation beside a running one, whose answer
        // is still there on the next resume.
        if (!cancelled) setIsResumeSettled(true)
      })

    return () => {
      cancelled = true
    }
  }, [isOpen, assistantId, isResumeSettled])

  // Poll the event log while a turn is in flight and the chat is open.
  useEffect(() => {
    if (!isOpen || !isAnswering || !assistantId || !conversationId) return

    let cancelled = false
    let timeout: ReturnType<typeof setTimeout> | undefined
    let emptyPolls = 0

    const describeError = (type: unknown) => {
      const key =
        typeof type === "string" && type in ERROR_KEYS
          ? ERROR_KEYS[type as keyof typeof ERROR_KEYS]
          : "ai_assistant_chat.ERROR_GENERIC"
      return t(key)
    }

    const poll = async () => {
      try {
        const { data: events, meta } = await v3.getAiAssistantEvents.fetchQuery(
          [assistantId, conversationId, cursorRef.current]
        )
        if (cancelled) return

        // The whole batch is applied before polling stops: the terminal
        // batch is the one carrying the committed answer.
        cursorRef.current = meta.cursor
        setMessages((previous) => applyEvents(previous, events, describeError))

        if (meta.done) {
          setIsAnswering(false)
          setNotice(null)
          return
        }

        emptyPolls = events.length === 0 ? emptyPolls + 1 : 0
      } catch (error) {
        if (cancelled) return

        // A dropped connection is retried; anything the server refused is not.
        const isNetworkError = isFetchError(error) && error.didRequestFail
        if (!isNetworkError) {
          setMessages((previous) =>
            replaceAnswer(previous, errorMessage(describeError(null)))
          )
          setIsAnswering(false)
          return
        }
      }

      const delay =
        emptyPolls >= EMPTY_POLLS_BEFORE_SLOWING ? SLOW_POLL_MS : POLL_MS
      timeout = setTimeout(poll, delay)
    }

    poll()

    // Cancellation is logical: a request already in flight completes and is
    // discarded, and no further poll is scheduled. bonzai passes no AbortSignal.
    return () => {
      cancelled = true
      clearTimeout(timeout)
    }
  }, [isOpen, isAnswering, assistantId, conversationId, t])

  const handleSubmit = async (question: string) => {
    if (!assistantId) return

    setNotice(null)
    setMessages((previous) => [
      ...previous,
      { messageType: "Question", text: question },
      answerPlaceholder(),
    ])

    let id = conversationId
    try {
      id ??= (await createConversation.mutateAsync([assistantId])).data.id
      setConversationId(id)

      const { meta } = await createMessage.mutateAsync([
        assistantId,
        id,
        question,
      ])
      cursorRef.current = meta.cursor
      setIsAnswering(true)
    } catch (error) {
      const isBusy = isFetchError(error) && error.status === 409
      if (isBusy && id) {
        await reloadBusyConversation(assistantId, id)
        return
      }

      setMessages((previous) =>
        replaceAnswer(previous, errorMessage(genericError))
      )
    }
  }

  // A 409 means another turn (another tab, usually) owns this conversation, so
  // this tab's transcript is stale. The question was not accepted: drop it,
  // take the conversation over as the server has it, and follow that turn.
  const reloadBusyConversation = async (
    busyAssistantId: string,
    busyConversationId: string
  ) => {
    setNotice(t("ai_assistant_chat.BUSY"))
    setIsReloading(true)
    try {
      // Looked up on the first page of conversations. One that has fallen
      // off it (dozens of resets in another tab) is treated as a failed reload.
      const loaded = await loadConversation(busyAssistantId, busyConversationId)
      if (!loaded) throw new Error("Busy conversation not found")
      adoptConversation(loaded)
      // The other turn may have finished before the reload. Then nothing will
      // poll, so nothing else would clear the notice.
      if (loaded.conversation.status !== "running") setNotice(null)
    } catch {
      // Drop the unaccepted question and its placeholder; the learner can
      // ask again once the other turn has finished.
      setMessages((previous) => previous.slice(0, -2))
      setNotice(genericError)
    } finally {
      setIsReloading(false)
    }
  }

  // The next send creates a new conversation. Resume has already settled, so
  // nothing reselects the one just left.
  const handleReset = () => {
    setMessages([])
    setConversationId(null)
    setNotice(null)
    cursorRef.current = 0
  }

  if (!chatConfig) return null

  const assistants = allChatConfigs.map((config) => ({
    id: config.id,
    title: config.title,
    avatar: config.assistantAvatar,
  }))

  // Submitting before the resume settles would race it for the transcript.
  const isPendingAnswer =
    !isResumeSettled ||
    isReloading ||
    createConversation.isPending ||
    createMessage.isPending ||
    isAnswering

  return (
    <Chat
      isOpen={isOpen}
      onClose={onClose}
      title={chatConfig.title}
      tagline={chatConfig.tagline}
      userAvatar={currentUser.avatar}
      assistantAvatar={chatConfig.assistantAvatar}
      questionSuggestions={chatConfig.questionSuggestions}
      onSubmit={handleSubmit}
      messages={messages}
      isPendingAnswer={isPendingAnswer}
      assistants={assistants}
      onSelectAssistant={selectAssistant}
      onReset={handleReset}
      // Resetting mid-turn would detach an answer the server is still writing.
      isResetDisabled={isPendingAnswer}
      notice={notice}
    />
  )
}

// Without an id, the latest conversation: newest created first, so a reset's
// new conversation is the one resumed next time.
//
// A `running` status is re-read after the messages, because the turn can
// settle in between: the messages would then end on that turn's answer and
// hand back its terminal cursor, and polling past it would never end. Status
// and terminal event are committed together, so a status still `running`
// after the messages were read means the turn is behind their cursor. The
// re-read is by id: "latest" may by then be a conversation another tab made.
const loadConversation = async (
  assistantId: string,
  conversationId?: string
) => {
  const findConversation = async (id?: string) => {
    const { data: conversations } =
      await v3.getAiAssistantConversations.fetchQuery([assistantId])
    return id ? conversations.find((row) => row.id === id) : conversations[0]
  }

  const loadMessages = (id: string) => {
    return v3.getAiAssistantMessages.fetchQuery([assistantId, id])
  }

  const conversation = await findConversation(conversationId)
  if (!conversation) return null

  let messages = await loadMessages(conversation.id)
  if (conversation.status !== "running") {
    return {
      conversation,
      history: messages.data,
      cursor: messages.meta.cursor,
    }
  }

  // Gone from the first page between the reads: its status can't be
  // verified, and a stale `running` would poll forever. Treat it as not found.
  const current = await findConversation(conversation.id)
  if (!current) return null

  if (current.status !== "running") {
    // Settled while the messages were loading: read the finished transcript.
    messages = await loadMessages(conversation.id)
  }

  return {
    conversation: current,
    history: messages.data,
    cursor: messages.meta.cursor,
  }
}

// History arrives newest first. Rows without text (none are expected from the
// learner path, but the column is nullable) have nothing to show.
const toChatMessages = (history: AiAssistantMessage[]): ChatMessage[] => {
  return [...history].reverse().flatMap((message): ChatMessage[] => {
    if (!message.content) return []
    if (message.role === "user") {
      return [{ messageType: "Question", text: message.content }]
    }
    if (message.role === "assistant") {
      return [{ messageType: "Response", text: message.content }]
    }
    return []
  })
}

// Payloads are unchecked at the schema (see `AiAssistantEvent`), so each field
// is narrowed where it is read.
const payloadString = (payload: unknown, key: string) => {
  if (
    typeof payload !== "object" ||
    payload === null ||
    Array.isArray(payload)
  ) {
    return undefined
  }
  const value = (payload as Record<string, unknown>)[key]
  return typeof value === "string" ? value : undefined
}

const answerPlaceholder = (): ChatMessage => ({
  messageType: "Response",
  text: "",
})

const errorMessage = (text: string): ChatMessage => ({
  messageType: "Error",
  text,
})

const replaceAnswer = (messages: ChatMessage[], answer: ChatMessage) => {
  return [...messages.slice(0, -1), answer]
}

// `token_batch` is a provisional preview and is appended; `done` carries the
// committed answer and replaces it, since a recovered turn can show a partial
// preview and then settle with the full answer.
const applyEvents = (
  messages: ChatMessage[],
  events: AiAssistantEvent[],
  describeError: (type: unknown) => string
) => {
  return events.reduce((current, event) => {
    // Never write over the learner's own question.
    const answer = current.at(-1)
    if (!answer || answer.messageType === "Question") return current

    switch (event.kind) {
      case "token_batch": {
        const text = payloadString(event.payload, "text") ?? ""
        return replaceAnswer(current, { ...answer, text: answer.text + text })
      }
      case "done": {
        // `done` ends polling, so an answer it does not carry would otherwise
        // leave an empty response on screen.
        const content = payloadString(event.payload, "content")
        if (content === undefined) {
          return replaceAnswer(current, errorMessage(describeError(null)))
        }
        return replaceAnswer(current, {
          messageType: "Response",
          text: content,
        })
      }
      case "error":
        return replaceAnswer(
          current,
          errorMessage(describeError(payloadString(event.payload, "type")))
        )
      default:
        return current
    }
  }, messages)
}
